// src/components/Teabates/TeabateArgumentReply.tsx
import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from 'config/axiosConfig';
import { TeabateArgumentObject } from './types';
import { useUser } from 'util/UserContext';
import { apiRoutes } from "util/apiRoutes";
import AiArgumentEvaluationPanel from "components/AI/AiArgumentEvaluationPanel";


interface TeabateArgumentReplyProps {
  teabateId: number;
  parentArgument?: TeabateArgumentObject;
  onReplySubmitted: () => void;
  onCancel: () => void;
}

const TeabateArgumentReply: React.FC<TeabateArgumentReplyProps> = ({
  teabateId,
  parentArgument,
  onReplySubmitted,
  onCancel
}) => {
  const { currentUser, setShowAuthModal } = useUser();
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [selectedType, setSelectedType] = useState<'agree' | 'disagree'>(
    parentArgument 
      ? (parentArgument.properties.argument_type === 'agree' ? 'disagree' : 'agree')
      : 'agree'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    
    if (!content.trim()) {
      setError('Please enter your argument');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const payload = {
        content,
        argument_type: selectedType,
        parent_id: parentArgument?.id,
        is_anonymous: isAnonymous,
      };
      
      const r = await api.post(apiRoutes.comments.teabateArgument(teabateId), payload);
      const d: any = r.data;
      if (d?.properties?.privacy === "anonymous" && d?.anon_edit_token) {
        const { saveAnonToken } = await import("util/anonTokenVault");
        const { addAnonRef } = await import("util/anonIndex");
        await saveAnonToken("comment", d.id, d.anon_edit_token);
        addAnonRef({ type: "comment", id: d.id });
      }
      setContent('');
      setIsAnonymous(false);
      onReplySubmitted();
    } catch (error) {
      console.error('Error submitting argument:', error);
      setError('Failed to submit your argument. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="text-white p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium">
          {parentArgument 
            ? `Reply with ${selectedType}`
            : 'Add your argument'}
        </h3>
        <button onClick={onCancel} className="text-gray-400">
          <X size={18} />
        </button>
      </div>
      
      <div className="flex mb-3 bg-gray-800 p-1 rounded-full">
        <button 
          className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
            selectedType === 'agree' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setSelectedType('agree')}
        >
          agree
        </button>
        <button 
          className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
            selectedType === 'disagree' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setSelectedType('disagree')}
        >
          disagree
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your argument here..."
          className="w-full bg-gray-800 border border-gray-700 rounded text-white text-sm mb-3 p-3"
          rows={4}
          required
        />

        <div className="mb-3">
          <AiArgumentEvaluationPanel
            claim=""
            draftArgument={content}
            onApplyRevision={setContent}
            parentArgumentId={parentArgument?.id}
            stance={selectedType}
            teabateId={teabateId}
          />
        </div>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="anonymous-argument"
            checked={isAnonymous}
            onChange={() => setIsAnonymous(!isAnonymous)}
            className="mr-2"
          />
          <label htmlFor="anonymous-argument" className="text-xs">
            Post anonymously
          </label>
        </div>
        
        {error && (
          <div className="text-red-400 text-xs mb-3">
            {error}
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 mr-2 text-xs py-2 px-3 rounded"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !content.trim()}
            className={`text-xs py-2 px-3 rounded ${
              isSubmitting || !content.trim() 
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeabateArgumentReply;
